import { useEffect, useMemo, useRef } from 'react';
import { InspectionListItem } from '../../../lib/inspections';
import { buildDaySummaryMap } from '../lib/daySummary';
import CalendarDayCell from './CalendarDayCell';

interface ScheduleCalendarProps {
  year: number;
  month: number; // 0-indexed (0 = January)
  inspections: InspectionListItem[];
  selectedDate: string | null; // "YYYY-MM-DD"
  onDateClick: (date: string | null) => void;
  onMonthChange?: (year: number, month: number) => void;
  collapsed?: boolean; // 3주 축소 뷰 (모바일/폴드 날짜 선택 시)
  centerDate?: string | null; // 축소 뷰에서 표시할 중심 날짜 (스크롤로 이동 시 기준, 없으면 selectedDate 사용)
  onWeekChange?: (delta: number) => void; // 축소 뷰에서 주 이동 (+7 / -7)
  externalSlideDirection?: 'left' | 'right' | null; // 부모가 주입하는 월 이동 방향
  /** 월간 뷰 제스처 패닝(휠/스와이프/드래그) → 월 이동. 미전달 시 기존 동작 그대로 (+1=다음달) */
  onMonthPan?: (direction: 1 | -1) => void;
}

interface CalendarDay {
  dateKey: string; // "YYYY-MM-DD"
  isCurrentMonth: boolean;
}

const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토'];

export default function ScheduleCalendar({
  year,
  month,
  inspections,
  selectedDate,
  onDateClick,
  onMonthChange,
  collapsed = false,
  centerDate,
  onWeekChange,
  externalSlideDirection = null,
  onMonthPan,
}: ScheduleCalendarProps) {
  const lastScrollTime = useRef(0);
  const SCROLL_THROTTLE = 400; // ms (축소 뷰 주 이동)
  const MONTH_PAN_COOLDOWN = 600; // ms (월 스냅 패닝 — 트랙패드 관성 연타 방어)
  const containerRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const animatingRef = useRef(false);

  // ref로 DOM 직접 제어하는 페이드 함수
  const triggerFade = () => {
    const el = gridRef.current;
    if (!el || animatingRef.current) return;
    animatingRef.current = true;

    el.style.transition = 'none';
    el.style.opacity = '0';

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        el.style.transition = 'opacity 200ms ease-in-out';
        el.style.opacity = '1';

        const onEnd = () => {
          el.style.transition = '';
          el.style.opacity = '';
          animatingRef.current = false;
          el.removeEventListener('transitionend', onEnd);
        };
        el.addEventListener('transitionend', onEnd, { once: true });
      });
    });
  };

  // 월 이동 애니메이션 (externalSlideDirection 변경 시)
  const prevExtDir = useRef<'left' | 'right' | null>(null);
  useEffect(() => {
    if (externalSlideDirection === null) return;
    if (externalSlideDirection === prevExtDir.current) return;
    prevExtDir.current = externalSlideDirection;
    // left(다음달): 오른쪽에서 진입, right(이전달): 왼쪽에서 진입
    triggerFade();
  }, [externalSlideDirection]); // eslint-disable-line react-hooks/exhaustive-deps

  // passive: false로 네이티브 wheel 이벤트 등록 (React onWheel은 passive라 preventDefault 불가)
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const handler = (e: WheelEvent) => {
      if (collapsed) {
        e.preventDefault();
        const now = Date.now();
        if (now - lastScrollTime.current < SCROLL_THROTTLE) return;
        lastScrollTime.current = now;

        if (onWeekChange) {
          // 축소 뷰: 주 단위 이동 (아래 스크롤 → 다음 주, 위 스크롤 → 이전 주)
          triggerFade();
          onWeekChange(e.deltaY > 0 ? 7 : -7);
        }
        return;
      }

      // 월간 뷰: 휠 → 월 스냅 패닝 (아래 스크롤 → 다음 달)
      if (!onMonthPan) return;
      e.preventDefault();
      if (Math.abs(e.deltaY) < 8) return; // 트랙패드 관성 꼬리 무시
      const now = Date.now();
      if (now - lastScrollTime.current < MONTH_PAN_COOLDOWN) return;
      lastScrollTime.current = now;
      triggerFade();
      onMonthPan(e.deltaY > 0 ? 1 : -1);
    };

    el.addEventListener('wheel', handler, { passive: false });
    return () => el.removeEventListener('wheel', handler);
  }, [collapsed, onWeekChange, onMonthPan]); // eslint-disable-line react-hooks/exhaustive-deps

  const today = useMemo(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }, []);

  // 날짜별 점검 집계 (담당자 배지 포함) — 월간/주간 공용 헬퍼
  const dayMap = useMemo(() => buildDaySummaryMap(inspections), [inspections]);

  // 월간 뷰 터치 스와이프/마우스 드래그 패닝 (수평 제스처 → 월 이동)
  const panStartRef = useRef<{ x: number; y: number } | null>(null);
  const panDraggedRef = useRef(false);

  const handlePanPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (collapsed || !onMonthPan) return;
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    panStartRef.current = { x: e.clientX, y: e.clientY };
    panDraggedRef.current = false;
  };

  const handlePanPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const start = panStartRef.current;
    if (!start) return;
    const dx = e.clientX - start.x;
    const dy = e.clientY - start.y;
    if (!panDraggedRef.current && Math.abs(dx) > 8 && Math.abs(dx) > Math.abs(dy)) {
      panDraggedRef.current = true;
      // 수평 드래그로 확정된 뒤에만 capture (일반 클릭 경로 미간섭)
      e.currentTarget.setPointerCapture(e.pointerId);
    }
    if (panDraggedRef.current && gridRef.current) {
      // 저항 있는 라이브 팔로우 (±60px 캡) — release 시 원복 또는 월 전환
      const capped = Math.max(-60, Math.min(60, dx * 0.35));
      gridRef.current.style.transform = `translateX(${capped}px)`;
    }
  };

  const handlePanPointerEnd = (e: React.PointerEvent<HTMLDivElement>) => {
    const start = panStartRef.current;
    if (!start) return;
    panStartRef.current = null;
    if (gridRef.current) gridRef.current.style.transform = '';
    if (!panDraggedRef.current || !onMonthPan) return;
    const dx = e.clientX - start.x;
    const dy = e.clientY - start.y;
    if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy)) {
      const now = Date.now();
      if (now - lastScrollTime.current >= MONTH_PAN_COOLDOWN) {
        lastScrollTime.current = now;
        triggerFade();
        // 왼쪽으로 스와이프(dx<0) → 다음 달 (콘텐츠가 왼쪽으로 밀려나는 방향)
        onMonthPan(dx < 0 ? 1 : -1);
      }
    }
  };

  // 드래그 직후 셀 클릭(날짜 선택) 억제
  const handlePanClickCapture = (e: React.MouseEvent<HTMLDivElement>) => {
    if (panDraggedRef.current) {
      panDraggedRef.current = false;
      e.preventDefault();
      e.stopPropagation();
    }
  };

  // 달력 그리드 계산
  const calendarDays = useMemo<CalendarDay[]>(() => {
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDow = firstDay.getDay(); // 0=Sun
    const totalDays = lastDay.getDate();

    const days: CalendarDay[] = [];

    // 이전 달 날짜로 앞 padding 채우기
    if (startDow > 0) {
      const prevMonthLastDay = new Date(year, month, 0).getDate();
      for (let i = startDow - 1; i >= 0; i--) {
        const d = prevMonthLastDay - i;
        const prevMonth = month === 0 ? 12 : month;
        const prevYear = month === 0 ? year - 1 : year;
        const dateKey = `${prevYear}-${String(prevMonth).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
        days.push({ dateKey, isCurrentMonth: false });
      }
    }

    // 현재 달 날짜
    for (let d = 1; d <= totalDays; d++) {
      const dateKey = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      days.push({ dateKey, isCurrentMonth: true });
    }

    // 다음 달 날짜로 뒤 padding 채우기 (최소 35칸(5주) 보장)
    // 달력은 항상 다음달 첫 주(1행)를 표시하여 현재 달의 마지막 날이 토요일이어도 다음달 날짜를 노출
    const nextMonth = month === 11 ? 1 : month + 2;
    const nextYear = month === 11 ? year + 1 : year;
    let nextDay = 1;
    while (days.length % 7 !== 0) {
      const dateKey = `${nextYear}-${String(nextMonth).padStart(2, '0')}-${String(nextDay).padStart(2, '0')}`;
      days.push({ dateKey, isCurrentMonth: false });
      nextDay++;
    }
    // 4주(28칸)로 딱 맞아 다음달 날짜가 한 칸도 없는 경우, 다음달 첫 주 1행 추가
    if (days.length < 35) {
      while (days.length < 35) {
        const dateKey = `${nextYear}-${String(nextMonth).padStart(2, '0')}-${String(nextDay).padStart(2, '0')}`;
        days.push({ dateKey, isCurrentMonth: false });
        nextDay++;
      }
    }

    return days;
  }, [year, month]);

  // 날짜 문자열("YYYY-MM-DD")에서 Date 객체를 생성하는 헬퍼
  const parseDateKey = (dateKey: string): Date => {
    const [y, m, d] = dateKey.split('-').map(Number);
    return new Date(y, m - 1, d);
  };

  // Date 객체를 "YYYY-MM-DD" 문자열로 변환하는 헬퍼
  const toDateKey = (date: Date): string => {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  };

  // collapsed 모드: centerDate(없으면 selectedDate) 기준 전주/현재주/다음주 (21칸)만 렌더링
  const visibleDays = useMemo<CalendarDay[]>(() => {
    const anchorDate = centerDate ?? selectedDate;
    if (!collapsed || !anchorDate) return calendarDays;

    // anchorDate 기준으로 전주 일요일 계산 (calendarDays 범위와 무관하게 직접 계산)
    const anchor_ = parseDateKey(anchorDate);
    const anchorDow = anchor_.getDay();
    // anchor 주의 일요일 (행의 첫날)
    const anchorWeekSunday = new Date(anchor_);
    anchorWeekSunday.setDate(anchor_.getDate() - anchorDow);
    // 전주 일요일 = anchorWeekSunday - 7일
    const prevWeekSunday = new Date(anchorWeekSunday);
    prevWeekSunday.setDate(anchorWeekSunday.getDate() - 7);

    // calendarDays 인덱스 맵 (빠른 조회용)
    const calendarDaysMap = new Map(calendarDays.map((d) => [d.dateKey, d]));

    // 21칸 생성: calendarDays에 있으면 그대로 사용, 없으면 직접 계산
    const result: CalendarDay[] = [];
    for (let offset = 0; offset < 21; offset++) {
      const targetDate = new Date(prevWeekSunday);
      targetDate.setDate(prevWeekSunday.getDate() + offset);
      const dateKey = toDateKey(targetDate);
      // calendarDays에 같은 dateKey가 있으면 isCurrentMonth 정보 재사용
      const existing = calendarDaysMap.get(dateKey);
      if (existing) {
        result.push(existing);
      } else {
        const isCurrentMonth = targetDate.getMonth() === month && targetDate.getFullYear() === year;
        result.push({ dateKey, isCurrentMonth });
      }
    }

    return result;
  }, [collapsed, centerDate, selectedDate, calendarDays, month, year]);

  const monthPanEnabled = !collapsed && !!onMonthPan;

  return (
    <div
      ref={containerRef}
      className="overflow-hidden rounded-card-lg border border-line bg-white shadow-card-sm"
      style={monthPanEnabled ? { touchAction: 'pan-y' } : undefined}
      onPointerDown={handlePanPointerDown}
      onPointerMove={handlePanPointerMove}
      onPointerUp={handlePanPointerEnd}
      onPointerCancel={handlePanPointerEnd}
      onClickCapture={handlePanClickCapture}
    >
      {/* 요일 헤더 */}
      <div className="grid grid-cols-7 border-b border-line bg-surface-soft">
        {WEEKDAYS.map((day, i) => (
          <div
            key={day}
            className={`py-2.5 text-center text-xs font-bold tracking-wider ${
              i === 0 ? 'text-rose-500' : i === 6 ? 'text-blue-500' : 'text-ink-500'
            }`}
          >
            {day}
          </div>
        ))}
      </div>

      {/* 날짜 그리드 */}
      <div className="overflow-hidden">
      <div ref={gridRef} className="grid grid-cols-7">
        {visibleDays.map(({ dateKey, isCurrentMonth }, idx) => {
          const isSelected = dateKey === selectedDate;

          const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
            (e.currentTarget as HTMLButtonElement).blur();
            if (!isCurrentMonth && onMonthChange) {
              // 이전/다음 달 날짜 클릭 시 해당 월로 전환
              const [clickedYear, clickedMonth] = dateKey.split('-').map(Number);
              onMonthChange(clickedYear, clickedMonth - 1); // 0-indexed
              onDateClick(dateKey);
            } else {
              if (!isSelected) onDateClick(dateKey);
              // 이미 선택된 날짜 재클릭 시 아무것도 하지 않음 (선택 유지)
            }
          };

          return (
            <CalendarDayCell
              key={`${dateKey}-${isCurrentMonth}`}
              dateKey={dateKey}
              isCurrentMonth={isCurrentMonth}
              summary={dayMap.get(dateKey)}
              isToday={dateKey === today}
              isSelected={isSelected}
              dow={idx % 7}
              onClick={handleClick}
            />
          );
        })}
      </div>
      </div>
    </div>
  );
}
